import { get, each, find, invoke, isNaN, isEmpty, map } from 'lodash';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import ThankYouPage from './components/thankYouPage';
import InitOfferwall from './offerwall';
import ScreenOutPage from './components/screenOutPage';
import UnlockScreen from './offerwall/unlockSurveysScreen';
import Loading from './components/loading';
import FetchAdditionalSurveysScreen from './components/fetchAdditionalSurveysScreen';
import Results from './components/playful/results';
import LanguageSelectModal from './components/languageSelectModal';
import Homescreen from './components/Homescreen';
import WaitScreen from './components/WaitScreen';
import { FocusElement } from './helpers/focusElement';
import {
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_DESCRIPTION,
  QUESTION_RANKING,
  QUESTION_SLIDER,
  QUESTION_RATING,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERICAL_OPEN_ENDED,
  ANSWER_OTHER,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_NPS,
  MINIMUM_POSTAL_CODE_LENGTH,
  DISQUALIFICATION_USER_UNDERAGE,
  INTERACTIVE_MEDIA_TYPES,
  SURVEY_STATE,
  QUESTION_MATRIX_VAN_WESTENDORP,
  QUESTION_MATRIX_BIPOLAR,
  RECRUITMENT_CAMPAIGN_SWAGBUCKS,
  RECRUITMENT_CAMPAIGN_BANNER_SWAGBUCKS,
  AVAILABLE_RECRUITMENT_CAMPAIGNS,
  RECRUITMENT_CAMPAIGN_TEST_BANNER,
  QUESTION_MATRIX_MAXDIFF,
  QUESTION_DRILLDOWN,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
  OFFERS_CONTAINER_CLASS_NAME,
  SURVEYS_CONTAINER_CLASS_NAME,
  FILTERS_CONTAINER_CLASS_NAME,
  OFFERS_POPUP_CONTAINER_ID_NAME,
} from './constants';
import translations from './translations';
import feedbackSurvey from './data/feedbackSurvey';
import Survey from './components/survey';
import { overrideBrandUI } from './ui-functions';
import {
  globeIcon,
  chevronIcon,
  volumeIcon,
  muteIcon,
} from './components/icons';
import {
  getCarriedForwardAnswers,
  getLanguageForKey,
  roundNumberToStringWithDecimals,
} from './utils';
import checkAndEnterSurvey from './components/survey/checkAndEnterSurvey';
import request from './request';
import { publicAssetsPrefix } from '../../../configuration';
import ReactQuestion from './reactComponents/Question';
import isOpenEndedValid from './reactComponents/QuestionBody/OpenEnded/isValid';
import isDrilldownValid from './reactComponents/QuestionBody/DrilldownQuestion/isValid';
import isOpenEndedNumericOrConstantSumValid from './reactComponents/QuestionBody/Matrix/MatrixOpenEnded/isValid';
import {
  VAN_WESTENDORP_QUESTION_DECIMALS,
  VAN_WESTENDORP_QUESTION_MAX_PRICE,
  VAN_WESTENDORP_QUESTION_MIN_PRICE,
  VAN_WESTENDORP_QUESTION_SORT_ASCENDING,
  VAN_WESTENDORP_QUESTION_SORT_DESCENDING,
} from './reactComponents/QuestionBody/MatrixVanWestendorp/constants';
import OfferwallFilters from './reactComponents/OfferwallFilters';
import Offers from './reactComponents/Offers';
import OfferPopup from './reactComponents/Offers/OfferPopup';

Pollfish.ui = (function ui() {
  return {
    // hold if scroll is fixed for older versions
    scroll: true,
    notifications: [],

    // get texts like terms etc based on the language of the device
    getFallbackText() {
      // fallback to english translations
      if (isEmpty(Pollfish.helper.json.translations)) {
        return translations;
      }
      return Pollfish.helper.json.translations;
    },

    getText(key, value) {
      // need to have a fallback text for the specific translation
      // in case of Pollfish.ui.texts are empty for some reason
      let text = translations[`mobile.${key}`];
      if (!isEmpty(Pollfish.ui.texts)) {
        text = Pollfish.ui.texts[`mobile.${key}`];
      }

      if (key === 'surveyPrompt' || key === 'surveyPromptNoIncentive') {
        return text && text.replace('_surveyLOI_', value);
      }
      if (key === 'warnings.max_decimals') {
        return text && text.replace('XXXX', value);
      }
      if (key === 'matrixVanWestendorp.warnings.min') {
        return text && text.replace('XXX', value);
      }
      if (key === 'matrixVanWestendorp.warnings.max') {
        return text && text.replace('XXX', value);
      }
      if (key === 'warnings.matrixNumericOpenEndedRange') {
        return text && text.replace('XXX', value.min).replace('YYY', value.max);
      }
      return text;
    },

    getRecruitmentBannerImage(appName) {
      const userLang = get(Pollfish, 'helper.json.user_lang', null);
      const showTestBanner =
        Pollfish.helper.isDevelopment() || Pollfish.helper.isCypressEnv();
      // return a test image for testing purpose
      if (appName && showTestBanner) {
        return RECRUITMENT_CAMPAIGN_TEST_BANNER;
      }

      const hasCustomTheme = Boolean(Pollfish.helper.hasCustomSurveyTheme());

      if (appName === RECRUITMENT_CAMPAIGN_SWAGBUCKS && !hasCustomTheme) {
        const swagbucksDefault = RECRUITMENT_CAMPAIGN_BANNER_SWAGBUCKS.en;
        return (
          RECRUITMENT_CAMPAIGN_BANNER_SWAGBUCKS[userLang] || swagbucksDefault
        );
      }
      return null;
    },

    // hide an element
    hide(div) {
      $(div).css({ opacity: 0 });
    },

    // get progress bar status based on the index of the question answered
    getProgressBarStatus() {
      const questionsAnswered = Pollfish.helper.getQuestionIndex();
      const questionsCount = Pollfish.store.totalQuestions;
      let percent = (questionsAnswered / questionsCount) * 100;
      // can happen if for example we loop over the same sanity twice so we have
      // "more" questions answered than the ones expected by the totalQuestions array
      if (percent > 100) {
        percent = 100;
      }
      return `${percent}%`;
    },

    // apply selected template result based on the type of the question
    templateResultChoiceShort(question) {
      return new Results(question).render();
    },

    applyMainContentTemplate(template) {
      Pollfish.ui.unmountReactRoot();
      $('.main-content').html(template);
    },

    unmountReactRoot() {
      // not sure if mount/unmount is always a good idea but i don't know what kind of side effects
      // will exist when jQuery also affects the same root
      if (Pollfish.reactRoot) {
        const container = document.querySelector('.main-content');
        Pollfish.reactRoot.render(null, container);
      }
    },

    unmountReactOfferwallFiltersComponent() {
      // not sure if mount/unmount is always a good idea but i don't know what kind of side effects
      // will exist when jQuery also affects the same root
      if (Pollfish.reactRootOfferwallFilters) {
        const container = document.querySelector(
          `.${FILTERS_CONTAINER_CLASS_NAME}`,
        );
        Pollfish.reactRootOfferwallFilters.render(null, container);
      }
    },

    unmountReactOfferwallOffersComponent() {
      // not sure if mount/unmount is always a good idea but i don't know what kind of side effects
      // will exist when jQuery also affects the same root
      if (Pollfish.reactRootOfferwallOffers) {
        const container = document.querySelector(
          `.${OFFERS_CONTAINER_CLASS_NAME}`,
        );
        Pollfish.reactRootOfferwallOffers.render(null, container);
      }
    },

    unmountReactOfferwallOffersPopupComponent() {
      // not sure if mount/unmount is always a good idea but i don't know what kind of side effects
      // will exist when jQuery also affects the same root
      if (Pollfish.reactRootOfferwallOffersPopup) {
        const container = document.querySelector(
          `#${OFFERS_POPUP_CONTAINER_ID_NAME}`,
        );
        Pollfish.reactRootOfferwallOffersPopup.render(null, container);
      }
    },

    // apply the corresponding template based on the current screen
    applyTemplate(question) {
      // return the correct template

      Raven.captureBreadcrumb({
        message: 'applyTemplate',
        category: 'applyTemplate',
        data: {
          question,
        },
      });

      // update the state/action respondent is into
      if (question && question.isTag) {
        Pollfish.surveyState.setState(SURVEY_STATE.ANSWERING_DEMOGRAPHIC);
      } else if (question && question.isProfilingQuestion) {
        Pollfish.surveyState.setState(SURVEY_STATE.ANSWERING_PROFILING);
      } else if (question && question.isSanityQuestion) {
        Pollfish.surveyState.setState(SURVEY_STATE.ANSWERING_SANITY);
      } else if (question && question.isScreening) {
        Pollfish.surveyState.setState(SURVEY_STATE.ANSWERING_SCREENING);
      } else if (question) {
        Pollfish.surveyState.setState(SURVEY_STATE.ANSWERING_SURVEY_QUESTION);
      }

      this.renderReactComponent(ReactQuestion, { question });

      setTimeout(() => {
        // handle elements for rating, sliders, ranking, open-ended etc
        Pollfish.ui.handleQuestionUIElements(question);

        Pollfish.ui.preselectQuestionAnswers(question);

        // handle text alignment
        Pollfish.helper.toggleRTL();

        // handle the progress bar
        $('#progressBar').width(Pollfish.ui.getProgressBarStatus());

        // get the current time the question was rendered
        Pollfish.helper.ttc.start = new Date();

        $('html, body').scrollTop(0);
      }, 0);
    },

    renderReactComponent(Component, data) {
      const container = document.querySelector('.main-content');
      // we need to clear the container because sometimes some jquery thing is already rendered there
      container.innerHTML = '';
      Pollfish.reactRoot = createRoot(container);
      Pollfish.reactRoot.render(
        <ErrorBoundary onError={Pollfish.helper.handleGlobalPromiseError}>
          <Component {...data} />
        </ErrorBoundary>,
      );
    },

    renderReactOfferwallFiltersComponent() {
      const container = document.querySelector(
        `.${FILTERS_CONTAINER_CLASS_NAME}`,
      );
      // we need to clear the container because sometimes some jquery thing is already rendered there
      container.innerHTML = '';
      Pollfish.reactRootOfferwallFilters = createRoot(container);
      Pollfish.reactRootOfferwallFilters.render(
        <ErrorBoundary onError={Pollfish.helper.handleGlobalPromiseError}>
          <OfferwallFilters />
        </ErrorBoundary>,
      );
    },

    renderReactOfferwallOffersComponent() {
      const container = document.querySelector(
        `.${OFFERS_CONTAINER_CLASS_NAME}`,
      );
      // we need to clear the container because sometimes some jquery thing is already rendered there
      container.innerHTML = '';
      container.setAttribute('id', 'js-offerwall-container-offers');
      Pollfish.reactRootOfferwallOffers = createRoot(container);
      Pollfish.reactRootOfferwallOffers.render(
        <ErrorBoundary onError={Pollfish.helper.handleGlobalPromiseError}>
          <Offers />
        </ErrorBoundary>,
      );
    },

    renderReactOfferwallOfferPopupComponent(offer) {
      const container = document.querySelector(
        `#${OFFERS_POPUP_CONTAINER_ID_NAME}`,
      );
      // we need to clear the container because sometimes some jquery thing is already rendered there
      container.innerHTML = '';
      container.setAttribute('id', 'js-offerwall-container-offers');
      Pollfish.reactRootOfferwallOffersPopup = createRoot(container);
      Pollfish.reactRootOfferwallOffersPopup.render(
        <ErrorBoundary onError={Pollfish.helper.handleGlobalPromiseError}>
          <OfferPopup
            offer={offer}
            onHide={Pollfish.ui.unmountReactOfferwallOffersPopupComponent}
          />
        </ErrorBoundary>,
      );
    },

    // handle elements for rating, sliders, ranking, open-ended etc
    handleQuestionUIElements(question) {
      // check if open-ended like other exists
      const openEndExists = question.answers.filter(
        (a) => a.style === ANSWER_OTHER,
      );

      let freeText = $('#freeText');
      if (
        question.style === QUESTION_OPEN_ENDED &&
        question.f_name === 'dg_postal_data'
      ) {
        freeText = $('#postalSearch');
      }
      const freeTextNumerical = $('#freeTextNumerical');

      // it open-ended exists handle keyboard resize
      // hide input from user
      // bind enter event to close keyboard
      if (openEndExists.length > 0) {
        // hide input from user
        $('#otherContainer').hide();

        const elem = $('#otherContainerInput');

        // bind enter event to close keyboard
        /* eslint-disable func-names */
        elem.on('keyup', function(e) {
          if (e.keyCode === 13) {
            $(this).val(
              $(this)
                .val()
                .replace(/\n+/g, ''),
            );
            $(this).blur();
          }
        });
        /* eslint-enable func-names */

        FocusElement(elem);
      }

      if (question.style === QUESTION_SLIDER) {
        // Pollfish.ui.slider.init();
      } else if (question.style === QUESTION_MATRIX_BIPOLAR) {
        // Pollfish.ui.bipolar.init();
      } else if (question.style === QUESTION_OPEN_ENDED) {
        // Open-Ended question

        FocusElement(freeText);
        // <-- 320 is the height of the keyboard
        if (
          freeText.offset().top +
            freeText.height() +
            $('.submit-btn-container').height() <
          $(window).height() - 320
        ) {
          if (Pollfish.helper.shouldAutofocus()) {
            setTimeout(() => {
              // in order to auto focus to the textarea we need both .tap and .focus, don't know why
              //
              // nrossolatos: The above was true because Apple doesn't like it when developers try
              // to enforce keyboard appearances when there is no user interaction. Faking a tap
              // would solve this issue. Unfortunately this will still not work for WKWebview.
              freeText.tap();
              freeText.focus();
            }, 100);
          }
        }

        $('#postalSearch').on('input', (event) => {
          Pollfish.event.addInteraction(event);
        });
        /* eslint-disable func-names */
        $('#postalSearch').on('keyup', function(event) {
          if (event.keyCode === 13) {
            $(this).val(
              $(this)
                .val()
                .replace(/\n+/g, ''),
            );
            $(this).blur();
          }
        });
        $('#postalSearch').on('paste', (event) => event.preventDefault());
        /* eslint-enable func-names */
      } else if (
        question.style === QUESTION_NUMERICAL_OPEN_ENDED ||
        question.f_name === 'dg_year_of_birth'
      ) {
        FocusElement(freeTextNumerical);

        /* eslint-disable func-names */
        freeTextNumerical.on('keyup', function(event) {
          if (event.keyCode === 13) {
            $(this).val(
              $(this)
                .val()
                .replace(/\n+/g, ''),
            );
            $(this).blur();
          }
        });
        /* eslint-enable func-names */

        if (
          freeTextNumerical.offset().top +
            freeTextNumerical.height() +
            $('.submit-btn-container').height() <
          $(window).height() - 320
        ) {
          if (Pollfish.helper.shouldAutofocus()) {
            freeTextNumerical.tap();
            freeTextNumerical.focus();
          }
        }
        // in van westendorp on focus of input we would like to scroll
        // to the parent element of the input which contains the question text
      } else if (question.style === QUESTION_MATRIX_VAN_WESTENDORP) {
        // eslint-disable-next-line func-names
        $('.input-van-westendorp').each(function() {
          const currentElement = $(this);
          const otherElement = $(currentElement).closest(
            '.answerMatrixVanWestendorp',
          );
          FocusElement(currentElement, otherElement);
        });
        // in open ended matrixes on focus of input we would like to scroll
        // to the parent element of the input which contains the question text
      } else if (
        question.style === QUESTION_MATRIX_OPEN_ENDED ||
        question.style === QUESTION_MATRIX_OPEN_ENDED_NUMERIC ||
        question.style === QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM
      ) {
        // eslint-disable-next-line func-names
        $('.answer--matrix-open-ended-answer-input').each(function() {
          const currentElement = $(this);
          const otherElement = $(currentElement).closest(
            '.answer--matrix-open-ended-answer-container',
          );
          FocusElement(currentElement, otherElement);
        });
      }
    },

    preselectQuestionAnswers(question) {
      if (!question) return;
      // always preselect the userLang for this demographic
      if (question.f_name === 'dg_speaking_langs') {
        const userLang = get(Pollfish, 'helper.json.user_lang', 'en');
        const preselectedAnswer = find(question.answers, ['f_value', userLang]);
        if (preselectedAnswer) {
          const domElement = $(`#${preselectedAnswer.id}`);
          domElement.addClass('answerClick');
        }
      }
    },

    // show prompt for a couple of seconds
    prompt(text, className) {
      Pollfish.ui.notifications.push({ text, className });
      Pollfish.ui.showNextPrompt();
    },

    promptSuccess(text) {
      Pollfish.ui.prompt(text, 'success');
    },

    promptError(text) {
      Pollfish.ui.prompt(text, 'error');
    },

    showNextPrompt() {
      // has already at least one running notification
      if (
        Pollfish.ui.notifications.length === 0 ||
        Pollfish.ui.notificationShowing
      ) {
        return;
      }
      Pollfish.ui.notificationShowing = true;
      const prompt = Pollfish.ui.notifications.pop();
      const promptInfo = $('#promptInfo');
      // makes cypress tests run a little faster not waiting for the next notification to show
      const promptTimeout = Pollfish.helper.isCypressEnv() ? 200 : 2000;

      // show prompt
      promptInfo
        .addClass(`prompt-info--visible ${prompt.className}`)
        .find('.prompt-info__text')
        .text(prompt.text);

      // hide prompt
      setTimeout(() => {
        promptInfo.removeClass('prompt-info--visible');
        // remove the color after a while if it existed
        setTimeout(() => {
          promptInfo.removeClass(prompt.className);
          Pollfish.ui.notificationShowing = false;
          Pollfish.ui.showNextPrompt();
        }, 250);
      }, promptTimeout);
    },

    clearNotificationPrompts() {
      const promptInfo = $('#promptInfo');
      promptInfo.removeClass('prompt-info--visible');
      Pollfish.ui.notificationShowing = false;
      Pollfish.ui.notifications = [];
    },

    showSlowDownWarning(responseTime) {
      if (Pollfish.helper.isPreview || Pollfish.helper.isDebugMode()) {
        return;
      }
      const text = Pollfish.ui.getText('read_questions_carefully');
      if (typeof Pollfish.helper.flag.showSlowDownWarning === 'undefined') {
        Pollfish.helper.flag.showSlowDownWarning = 0;
      }

      Pollfish.helper.flag.showSlowDownWarning += 1;
      if (
        responseTime <= 1000 ||
        Pollfish.helper.flag.showSlowDownWarning < 2
      ) {
        setTimeout(() => Pollfish.ui.prompt(text), 0);
      }
    },

    showConfirmationModal(props) {
      const {
        header = '',
        content = '',
        onConfirm = () => {},
        onCancel = () => {},
        useHtmlContent = false,
        noBtnText = Pollfish.ui.getText('no') || 'No',
        yesBtnText = Pollfish.ui.getText('yes') || 'Yes',
        style = undefined,
        buttonStack = 'horizontal',
      } = props;
      const container = $('#js-leave-survey-modal');
      if (style) {
        const modalContent = container.find('.modal-content');
        modalContent.removeAttr('style').css(style);
      }
      if (buttonStack === 'vertical') {
        container.addClass('vertical-button-stack');
      }
      container.addClass('open');
      container.find('.js-modal-header').text(header);
      if (useHtmlContent) {
        container.find('.js-modal-body').html(content);
      } else {
        container.find('.js-modal-body').text(content);
      }
      container.find('.js-modal-cancel').text(noBtnText);
      container.find('.js-modal-confirm').text(yesBtnText);
      container.find('.js-modal-cancel').on('tap', (e) => {
        e.preventDefault();
        Pollfish.ui.closeModal(container);
        onCancel();
      });

      container.find('.js-modal-confirm').on('tap', (e) => {
        e.preventDefault();
        Pollfish.ui.closeModal(container);
        onConfirm();
      });
    },

    closeModal(container) {
      container.removeClass('open').removeClass('vertical-button-stack');
      container.find('.js-modal-cancel').off('tap');
      container.find('.js-modal-confirm').off('tap');
    },

    showAvailableLanguages() {
      const html = $('html');
      const body = $('body');
      Raven.captureBreadcrumb({
        message: 'ShowAvailableLanguages',
        category: 'ShowAvailableLanguages',
      });

      // will reset offerwall html otherwise
      html
        .animate({ scrollTop: 0 }, 10)
        .promise()
        .done(() => {
          const container = $('#language-select-modal');
          // delete previous modals
          container.empty();
          const currentPage = body.attr('data-page');
          const modal = new LanguageSelectModal(container, () => {
            // on different language select
            Pollfish.ui.renderLanguageSelect();
            if (currentPage.match(/(homescreen-)/)) {
              const homescreen = new Homescreen().render();
              Pollfish.ui.applyMainContentTemplate(homescreen);
            } else if (currentPage.match(/(offerwall|unlock-screen|points)/)) {
              Pollfish.ui.showOfferwall();
            }
          });
          modal.show();
        });
    },

    renderLanguageSelect() {
      // we don't show the language select for users that have already chosen their language
      const hasAnsweredAllDemographicQuestions = get(
        Pollfish,
        'helper.json.hasAllDemographics',
        false,
      );
      const shouldNotChangeLang =
        hasAnsweredAllDemographicQuestions && !Pollfish.helper.isOfferwall();
      // or for production debug sessions
      const shouldHideLang =
        !Pollfish.helper.isDevelopment() && Pollfish.helper.isDebugMode();
      if (shouldNotChangeLang || shouldHideLang) {
        return;
      }

      const container = $('#js-languageSelect');

      const currentLangKey = invoke(Pollfish, 'ui.currentLang.toLowerCase');
      const language = getLanguageForKey(currentLangKey);
      container.html(`
        <div class="select__container">
          <div class="select__img-flag">
            ${globeIcon}
          </div>
          <span class="select__text">
            ${language.slice(0, 2)}
          </span>
          <span class="chevron">
            ${chevronIcon}
          </span>
        </div>
      `);

      if (!Pollfish.helper.hasRenderedLanguageSelectOnce) {
        Pollfish.helper.hasRenderedLanguageSelectOnce = true;
        container.on('tap', () => {
          Pollfish.ui.showAvailableLanguages();
        });
      }
    },

    overridePrimaryColorClasses(primaryColor, backgroundColor) {
      Pollfish.ui.removeBrandedTheme();

      if (primaryColor) {
        // grab either white or black based on given color
        const invertedColor = Pollfish.helper.getTextColor(primaryColor);
        $('head').append(`
          <style id="branded-theme">
            .color-primary {
              color: ${primaryColor} !important;
            }

            .btn-primary,
            .btn-w-icon {
              color: ${invertedColor};
              background-color: ${primaryColor} !important;
            }

            .answer.answerClick .answer__description,
            .answerClick.matrix__answer .answer__description,
            .answer--click .answer__description,
            .answer.answerClick:not(.answer--with-image),
            .answerClick.matrix__answer:not(.answer--with-image),
            .answer--click:not(.answer--with-image) {
              color: ${primaryColor};
              background-color: ${primaryColor}11;
            }

            .answer.answer--with-image.answerClick,
            .answer--with-image.answerClick.matrix__answer,
            .answer--click.answer--with-image {
              border-color: ${primaryColor};
            }

            .answer.answerClick .answer__description .answer__description:before,
            .answerClick.matrix__answer .answer__description .answer__description:before,
            .answer.answerClick .answer__description:before,
            .answerClick.matrix__answer .answer__description:before,
            .answer--click .answer__description .answer__description:before,
            .answer--click .answer__description:before,
            .answer.answerClick:not(.answer--with-image) .answer__description:before,
            .answerClick.matrix__answer:not(.answer--with-image) .answer__description:before,
            .answer.answerClick:not(.answer--with-image):before,
            .answerClick.matrix__answer:not(.answer--with-image):before,
            .answer--click:not(.answer--with-image) .answer__description:before,
            .answer--click:not(.answer--with-image):before,
            .matrix__selected-answer {
              color: ${primaryColor};
            }

            .qp-bar__number {
              color: ${primaryColor};
            }

            .qp-bar__line #progressBar {
              background-color: ${primaryColor};
            }

            .ui-btn-active,
            .ranking__answer-btn--selected {
              background-color: ${primaryColor} !important;
            }

            #wrapper {
              background-color: ${backgroundColor};
            }

            .scale-label.active,
            .scale-step.active,
            .ui-slider-value,
            .ui-btn-inner .ui-btn-text:before,
            .ui-btn-inner .ui-btn-text:after {
              color: ${primaryColor};
            }
            .ui-btn-inner {
              border-color: ${primaryColor};
            }


            .bipolar-horizontal-column.column-selected:before,
            .bipolar-vertical-column.column-selected:before {
              color: ${primaryColor};
            }

            .bipolar-vertical--checked:before {
              color: ${primaryColor};
            }

            .bipolar-slider-step,
            .bipolar-slider .ui-slider-handle {
              border-color: ${primaryColor};
            }
            .bipolar-slider .ui-slider-handle--touched {
              background-color: ${primaryColor};
            }
            .bipolar-slider .ui-slider-bg {
              background-color: #E1E6EC !important;
            }

            .offerwall-filters-tab-selected {
              color: ${primaryColor};
              border-bottom: 2px solid ${primaryColor};
            }
            .offerwall-offer-categories-btn-selected {
              background-color: ${primaryColor};
              color: ${invertedColor};
            }
            .offerwall-offer-categories-btn-selected span:before {
              color: ${invertedColor};
            }
            .offerwall-offer-categories-change {
              color: ${primaryColor};
            }

            .offerwall-offers-popup-actions-link-wrapper,
            .offerwall-offers-popup-actions-button-sdk {
              color: ${invertedColor};
              background-color: ${primaryColor};
            }

            .offerwall-offers-popup-actions-link {
              color: ${invertedColor};
            }

            .offerwall-offers-popup-device-selected {
              color: ${invertedColor};
              background-color: ${primaryColor};
              border: 1px solid ${primaryColor};
            }
            .offerwall-offers-popup-device-btn--save {
              color: ${invertedColor};
              background-color: ${primaryColor};
              border: 1px solid ${primaryColor};
            }

            .scrollbox {
              background-image:
                /* Shadows */
                linear-gradient(to right, ${backgroundColor}, ${backgroundColor}),
                linear-gradient(to right, ${backgroundColor}, ${backgroundColor}),
                /* Shadow covers */
                linear-gradient(to right, rgba(0,0,0,.1), rgba(255,255,255,0)),
                linear-gradient(to left, rgba(0,0,0,.1), rgba(255,255,255,0));
            }

          </style>
        `);
      }

      if (backgroundColor) {
        const backgroundInverted = Pollfish.helper.getTextColor(
          backgroundColor,
        );
        const rankingOpacity = backgroundInverted === '#000000' ? '0.3' : '0.7';
        $('head').append(`
          <style id="branded-theme-2">
            #wrapper {
              background-color: ${backgroundColor};
            }

            .ranking__answer-btn {
              background-color: ${backgroundInverted};
              color: ${backgroundColor};
            }

            .matrix__question,
            .matrix:before,
            .answers-wrapper--description,
            .scale-step,
            .scale-step.closer,
            .scale-step.close,
            .ranking__title,
            .ranking__answers-high,
            .ranking__answers-low,
            .scale-step.sticky,
            .scale-label,
            .ranking__title {
              color: ${backgroundInverted};
            }
            .ranking__answer-btn--disabled {
              opacity: ${rankingOpacity};
            }
            .matrix__hide-answers-btn {
              color: ${backgroundInverted};
              border-color: ${backgroundInverted} !important;
            }

            .unlock-screen--title h1,
            .unlock-screen--title p,
            .unlock-screen--acceptance-info,
            .footer__terms {
              color: ${backgroundInverted};
            }

            [data-page="gdpr"] h1,
            [data-page="gdpr"] h2,
            [data-page="gdpr"] p,
            [data-page="gdpr"] li {
              color: ${backgroundInverted};
            }

            .gdpr-footer--outer .gdpr-btn-container h1 {
              color: unset;
            }

            .points-container h1 {
              color: ${backgroundInverted}
            }
          </style>
        `);
      }
    },

    applyBrandedSurveyStyles() {
      const applyPrimaryColorClasses = () => {
        const headerLogoEl = document.querySelector('.header__logo');
        if (headerLogoEl) {
          const pollfishLogo = document.createElement('span');
          pollfishLogo.className = 'icon-Prodege_Pollfish';
          $(headerLogoEl).empty();
          headerLogoEl.appendChild(pollfishLogo);
        }

        Pollfish.ui.overridePrimaryColorClasses(
          Pollfish.helper.json.theme_primary_color,
          Pollfish.helper.json.theme_background_color,
        );
      };

      const surveyTheme = Pollfish.helper.getSurveyTheme();
      const surveyThemeWithSettings = Pollfish.helper.getSurveyThemeWithSettings();
      const isDefaultTheme = get(surveyThemeWithSettings, 'isDefault', false);
      const hasThemeParamsInUrl = Pollfish.helper.isBrandedSurveyFromParams();

      const currentPage = $('body').attr('data-page');

      // the offerwall check is placed because when we returned back to the offerwall page
      // after a completed survey with a researcher theme, the styles would stay there
      // because of not cleaning the survey
      if (Pollfish.helper.isOfferwall() && currentPage === 'offerwall') {
        applyPrimaryColorClasses();
      } else if (surveyTheme && (hasThemeParamsInUrl || !isDefaultTheme)) {
        const isPollfishTheme = Pollfish.helper.hasPollfishSurveyTheme();
        if (isPollfishTheme && isDefaultTheme && !hasThemeParamsInUrl) {
          // Default Pollfish theme with no url params
          applyPrimaryColorClasses();
        } else if (
          isPollfishTheme &&
          !isDefaultTheme &&
          (Pollfish.helper.json.theme_primary_color ||
            Pollfish.helper.json.theme_background_color)
        ) {
          // it's a non default Pollfish theme. i.e. Dark theme
          applyPrimaryColorClasses();
        } else {
          const hasAdvancedThemeColors = get(
            surveyThemeWithSettings,
            'advancedThemeSettingsEnabled',
            false,
          );
          overrideBrandUI(surveyTheme, hasAdvancedThemeColors);
        }
      } else {
        applyPrimaryColorClasses();
      }

      // Note: header padding is added for previewing mobile when on diy (Design request)
      const headerPaddingTop = Pollfish.helper.isPreview ? '12px' : 0;
      const headerEl = document.querySelector('.header');
      if (headerEl) {
        headerEl.style.paddingTop = headerPaddingTop;
      }
    },

    removeBrandedTheme() {
      const existingBrandedTheme = document.querySelector('#branded-theme');
      const existingBrandedTheme2 = document.querySelector('#branded-theme-2');
      if (existingBrandedTheme) {
        existingBrandedTheme.remove();
      }
      if (existingBrandedTheme2) {
        existingBrandedTheme.remove();
      }
    },

    // check if answer is valid and qualifies to next question
    isQuestionValid(question, answers) {
      if (
        Pollfish.ui.validRanking(question) &&
        Pollfish.ui.validMatrix(question) &&
        Pollfish.ui.validMinMaxNumberOfAnswers(question) &&
        Pollfish.ui.validMinMaxValue(question) &&
        Pollfish.ui.validMatrixVanWestendorp(question) &&
        Pollfish.ui.validOtherValue(question) &&
        Pollfish.ui.validMatrixMaxDiff(question) &&
        Pollfish.ui.validMatrixBipolar(question) &&
        isDrilldownValid(question, answers) &&
        isOpenEndedNumericOrConstantSumValid(question) &&
        isOpenEndedValid(question)
      ) {
        return true;
      }
      return false;
    },

    // check if other answer can be empty or not when selected
    // and question type is single on multiple select
    validOtherValue(question) {
      // if its a single or multiple question run validations
      // however if it is not return true to qualify
      if (
        question.style !== QUESTION_SINGLE &&
        question.style !== QUESTION_MULTIPLE
      ) {
        return true;
      }
      // find the other answer and check for the property
      const otherAnswer = find(question.answers, ['style', ANSWER_OTHER]);
      const otherRequired = get(otherAnswer, 'required', false);
      if (!otherRequired) {
        return true;
      }
      // if the other answer is not selected then return true to qualify
      const otherAnswerElement = $('.answer-other');
      const isOtherSelected = otherAnswerElement.hasClass('answerClick');
      if (!isOtherSelected) {
        return true;
      }
      // if the other answer is selected and the value is not empty
      // then return true to qualify
      const otherValue = Pollfish.helper.getTextFromField(
        '#otherContainerInput',
      );
      if (otherValue) {
        return true;
      }
      // show warning message for empty other answer and do not qualify
      Pollfish.ui.prompt(Pollfish.ui.getText('warnings.other_open_ended'));
      Pollfish.ui.scrollToElementPosition(otherAnswerElement);
      return false;
    },

    // check if min and max value in numerical open-ended is valid
    validMinMaxValue(question) {
      // if its a numerical open-ended question run validations
      // however if it is not return true to qualify
      if (question.style === QUESTION_NUMERICAL_OPEN_ENDED) {
        const inputVal = Pollfish.helper.getNumericValue();
        if (isNaN(inputVal)) {
          Pollfish.ui.prompt(Pollfish.ui.getText('warnings.numeric_open'));
          return false;
        }

        if (question.min !== undefined && inputVal < question.min) {
          // show prompt for a couple of seconds for minimum accepted value
          Pollfish.ui.prompt(
            `${Pollfish.ui.getText('warnings.numeric_with_min')} ${
              question.min
            }`,
          );
          return false;
        }

        if (question.max !== undefined && inputVal > question.max) {
          // show prompt for a couple of seconds for maximum accepted value
          Pollfish.ui.prompt(
            `${Pollfish.ui.getText('warnings.numeric_with_max')} ${
              question.max
            }`,
          );
          return false;
        }

        const { allowedDecimals = 0 } = question;
        const [, decimals = ''] = inputVal.toString().split('.');
        const numberOfDecimalsInInput = decimals.length;
        if (allowedDecimals === 0 && numberOfDecimalsInInput > 0) {
          Pollfish.ui.prompt(
            Pollfish.ui.getText('warnings.numeric_integer_only'),
          );
          return false;
        }
        if (numberOfDecimalsInInput > allowedDecimals) {
          Pollfish.ui.prompt(
            Pollfish.ui.getText(
              'warnings.max_decimals',
              allowedDecimals.toString(),
            ),
          );
          return false;
        }
        return true;
      }
      return true;
    },

    // check if min and max number of answers in multiple choice question is valid
    validMinMaxNumberOfAnswers(question) {
      // if its a multiple choice question run validations
      // however if it is not return true to qualify
      if (question.style === QUESTION_MULTIPLE) {
        // when none of the above is selected we ignore the limits
        if (Pollfish.event.noneOfTheAboveSelected()) {
          return true;
        }
        // when an exclusive answer is selected we ignore the limits
        if ($('.answerClick.exclusive-answer').length === 1) {
          return true;
        }
        const { min, max } = question;
        const numberOfAnswers = $('.answerClick').length;
        const needsExactAnswers = question.min === question.max;
        if (min !== undefined && needsExactAnswers && numberOfAnswers !== max) {
          // show prompt for a couple of seconds to inform about exact number of answers
          Pollfish.ui.prompt(
            `${Pollfish.ui.getText(
              'warnings.multiple_with_min',
            )} ${min} ${Pollfish.ui.getText('warnings.answers')}`,
          );

          return false;
        }
        if (min !== undefined && numberOfAnswers < min) {
          // show prompt for a couple of seconds to inform about minimum number of answers
          Pollfish.ui.prompt(
            `${Pollfish.ui.getText(
              'warnings.multiple_with_min',
            )} ${min} ${Pollfish.ui.getText('warnings.answers')}`,
          );

          return false;
        }

        if (max !== undefined && numberOfAnswers > max) {
          // show prompt for a couple of seconds to inform about maximum number of answers
          Pollfish.ui.prompt(
            `${Pollfish.ui.getText(
              'warnings.multiple_with_max',
            )} ${max} ${Pollfish.ui.getText('warnings.answers')}`,
          );

          return false;
        }

        return true;
      }
      return true;
    },

    // check if the ranking is valid in a ranking question type
    validRanking(question) {
      // if answer is ranking run validations
      // and if not return true
      if (question.style !== QUESTION_RANKING) {
        return true;
      }

      const domAnswers = $('.answerRanking');
      const answerWithoutRanking = find(
        domAnswers,
        (answer) => !$(answer).attr('data-selected-rank'),
      );
      if (!answerWithoutRanking) {
        return true;
      }
      Pollfish.ui.prompt(Pollfish.ui.getText('warnings.ranking'));
      setTimeout(() => {
        const scrollTop =
          $(answerWithoutRanking).position().top -
          $('.header').position().top -
          5;
        $('html, body').animate({ scrollTop }, 100);
      }, 300);

      return false;
    },

    // check if the selections are valid in a matrix question type
    validMatrix(question) {
      // if answer is matrix single choice run validations
      // and if not return true
      if (
        question.style === QUESTION_MATRIX_SINGLE ||
        question.style === QUESTION_MATRIX_MULTIPLE
      ) {
        const answerIds = [];
        const emptyAnswers = [];

        const matrixAnswers = getCarriedForwardAnswers(question);
        // const matrixScorepoints = Pollfish.helper.getCarriedForwardScorepoints(question);
        // iterate over all answers and get ranking values

        each($('.answerMatrix'), (domElement) => {
          const answerId = $(domElement).attr('data-answer-id');
          const selectedScorepointId = $(domElement).attr(
            'data-selected-scorepoint-id',
          );
          const belongsToAvailableAnswers = matrixAnswers.find(
            ({ id }) => id.toString() === answerId,
          );

          if (!belongsToAvailableAnswers) return;

          if (selectedScorepointId) {
            answerIds.push(selectedScorepointId);
          } else {
            emptyAnswers.push(domElement);
          }
        });

        // uniques should be the same as the answer length to make sure all are ranked
        // if it is less than the total answers length then prompt user to fix it
        if (answerIds.length < matrixAnswers.length) {
          Pollfish.ui.prompt(Pollfish.ui.getText('warnings.matrix_single'));

          // get the first answer without ranking
          const answerWithoutSelection = emptyAnswers.shift();

          setTimeout(() => {
            const newPosition =
              $(answerWithoutSelection).position().top -
              $('.header').position().top -
              5;
            $('html, body').animate({ scrollTop: newPosition }, 100);
          }, 300);

          return false;
        }
        return true;
      }
      return true;
    },

    // check if the selections are valid in a matrix van westendorp question type
    validMatrixVanWestendorp(question) {
      // if answer is matrix van westendorp run validations
      // and if not return true
      if (question.style !== QUESTION_MATRIX_VAN_WESTENDORP) {
        return true;
      }

      const options = get(
        question,
        'matrix.validation.vanWestendorpValidation',
        {},
      );
      const min = options.min || VAN_WESTENDORP_QUESTION_MIN_PRICE;
      const max = options.max || VAN_WESTENDORP_QUESTION_MAX_PRICE;
      const ordering =
        options.ordering || VAN_WESTENDORP_QUESTION_SORT_ASCENDING;
      const allowedDecimals =
        options.decimals || VAN_WESTENDORP_QUESTION_DECIMALS;
      const matrixAnswersLength = $('.answerMatrixVanWestendorp').length;
      const answersDomElements = [];
      const answerIds = [];
      const emptyAnswers = [];

      // iterate over all answers and get typed values
      each($('.answerMatrixVanWestendorp'), (domElement) => {
        answersDomElements.push(domElement);
        const answerText = $(domElement)
          .find('.input-van-westendorp')
          .val();

        if (!isNaN(parseFloat(answerText))) {
          answerIds.push(answerText);
        } else {
          emptyAnswers.push(domElement);
        }
      });

      // all inputs should have a value type if not show a prompt
      // and then scroll the view to the first empty input
      if (answerIds.length < matrixAnswersLength) {
        Pollfish.ui.prompt(
          Pollfish.ui.getText('matrixVanWestendorp.warnings.missingValue'),
        );

        // get the first answer without value
        const answerWithoutSelection = emptyAnswers.shift();
        Pollfish.ui.scrollToElementPosition(answerWithoutSelection);
        Pollfish.ui.matrixAnswerSlideDown(answerWithoutSelection);
        return false;
      }

      const answersAsNumbers = answerIds.map((answerValue) =>
        parseFloat(answerValue),
      );
      const notValidAnswerIndexMin = answersAsNumbers.findIndex(
        (answerValue) => answerValue < min,
      );
      if (notValidAnswerIndexMin > -1) {
        const minWithDecimals = roundNumberToStringWithDecimals(
          min,
          allowedDecimals,
        );
        // show prompt for a couple of seconds for minimum accepted value
        Pollfish.ui.prompt(
          Pollfish.ui.getText(
            'matrixVanWestendorp.warnings.min',
            minWithDecimals,
          ),
        );
        Pollfish.ui.scrollToElementPosition(
          answersDomElements[notValidAnswerIndexMin],
        );
        Pollfish.ui.matrixAnswerSlideDown(
          answersDomElements[notValidAnswerIndexMin],
        );
        return false;
      }

      const notValidAnswerIndexMax = answersAsNumbers.findIndex(
        (answerValue) => answerValue > max,
      );
      if (notValidAnswerIndexMax > -1) {
        const maxWithDecimals = roundNumberToStringWithDecimals(
          max,
          allowedDecimals,
        );
        // show prompt for a couple of seconds for maximum accepted value
        Pollfish.ui.prompt(
          Pollfish.ui.getText(
            'matrixVanWestendorp.warnings.max',
            maxWithDecimals,
          ),
        );
        Pollfish.ui.scrollToElementPosition(
          answersDomElements[notValidAnswerIndexMax],
        );
        Pollfish.ui.matrixAnswerSlideDown(
          answersDomElements[notValidAnswerIndexMax],
        );
        return false;
      }

      let invalidAnswerIndex = null;
      let translationKey = 'matrixVanWestendorp.warnings.orderAsc';
      if (ordering === VAN_WESTENDORP_QUESTION_SORT_DESCENDING) {
        translationKey = 'matrixVanWestendorp.warnings.orderDesc';
        answersAsNumbers.forEach((answerValue, index) => {
          if (index === 0 || invalidAnswerIndex !== null) {
            return;
          }
          if (answersAsNumbers[index] >= answersAsNumbers[index - 1]) {
            invalidAnswerIndex = index;
          }
        });
      } else {
        answersAsNumbers.forEach((answerValue, index) => {
          if (index === 0 || invalidAnswerIndex !== null) {
            return;
          }
          if (answersAsNumbers[index] <= answersAsNumbers[index - 1]) {
            invalidAnswerIndex = index;
          }
        });
      }

      if (invalidAnswerIndex === null) {
        return true;
      }

      Pollfish.ui.prompt(Pollfish.ui.getText(translationKey));
      Pollfish.ui.scrollToElementPosition(
        answersDomElements[invalidAnswerIndex],
      );
      Pollfish.ui.matrixAnswerSlideDown(answersDomElements[invalidAnswerIndex]);
      return false;
    },

    // check if the selections are valid in a matrix max diff question type
    validMatrixMaxDiff(question) {
      // if answer is matrix max diff run validations
      // and if not return true
      if (question.style !== QUESTION_MATRIX_MAXDIFF) {
        return true;
      }

      const bestElements = $('.answerClick[data-answer-best="true"]');
      const worstElements = $('.answerClick[data-answer-worst="true"]');

      // respondent can select only one best answer
      if (bestElements.length !== 1) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.single'));
        return false;
      }

      // respondent can select only one worst answer
      if (worstElements.length !== 1) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.single'));
        return false;
      }

      const bestElement = $(bestElements).first();
      const worstElement = $(worstElements).first();

      // an answer cannot be best and worst simultaneously
      if ($(bestElement).attr('id') === $(worstElement).attr('id')) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.single'));
        return false;
      }

      return true;
    },

    // check if the selections are valid in a matrix bipolar question type
    validMatrixBipolar(question) {
      // if answer is matrix bipolar run validations
      // and if not return true
      if (question.style !== QUESTION_MATRIX_BIPOLAR) {
        return true;
      }

      const answersDomElements = [];
      const emptyAnswers = [];

      each($('.answer--bipolar'), (domElement) => {
        answersDomElements.push(domElement);
        const columnId = $(domElement).attr('data-selected-column-id');

        if (isNaN(parseInt(columnId, 10))) {
          emptyAnswers.push(domElement);
        }
      });

      // all question answers should have a value if not show a prompt
      // and then scroll the view to the first empty input
      if (emptyAnswers.length > 0) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.matrix_bipolar'));

        // get the first answer without value
        const answerWithoutSelection = emptyAnswers.shift();
        Pollfish.ui.scrollToElementPosition(answerWithoutSelection);
        Pollfish.ui.matrixBipolarAnswerSlideDown(answerWithoutSelection);
        return false;
      }

      return true;
    },

    matrixAnswerSlideDown(element) {
      setTimeout(() => {
        $(element)
          .find('.matrix__answers')
          .removeClass('matrix__answers--closed')
          .slideDown();

        $(element)
          .closest('.matrix')
          .removeClass('matrix--closed');
      }, 300);
    },

    matrixBipolarAnswerSlideDown(element) {
      setTimeout(() => {
        $(element)
          .find('.bipolar-vertical-wrapper')
          .removeClass('bipolar-vertical-wrapper--closed')
          .slideDown();

        $(element)
          .closest('.bipolar-vertical')
          .removeClass('bipolar-vertical--closed');
      }, 300);
    },

    matrixOpenEndedAnswerSlideDown(element) {
      setTimeout(() => {
        $(element)
          .closest('.answer--matrix-open-ended-content-wrapper')
          .slideDown();

        $(element)
          .parents('.answer--matrix-open-ended')
          .find('.answer--matrix-open-ended-row-label')
          .removeClass('answer--matrix-open-ended-row-label--closed');
      }, 300);
    },

    scrollToElementPosition(element, timeout = 300) {
      setTimeout(() => {
        const newPosition =
          $(element).position().top - $('.header').position().top - 5;
        $('html, body').animate({ scrollTop: newPosition }, 100);

        const openEndedMatrixWrapper = $(element).parents(
          '.answer--matrix-open-ended-horizontal',
        );
        // if it is an open ended matrix horizontal then scroll the element position
        if (openEndedMatrixWrapper) {
          $(openEndedMatrixWrapper).animate(
            {
              scrollLeft:
                $(element).position().left +
                openEndedMatrixWrapper.scrollLeft() -
                12,
            },
            600,
          );
        }
      }, timeout);
    },

    // apply virtual taps for question with different interface
    // than single and multiple
    virtualTaps(question) {
      const freeTextValue = $.trim($('#freeText').val());
      const numericValue = Pollfish.helper.getNumericValue();
      const freeTextNumericalValue = numericValue.toString();
      // select default answer since they are not visible
      if (
        question.style === QUESTION_DESCRIPTION ||
        (question.style === QUESTION_OPEN_ENDED &&
          question.f_name !== 'dg_postal_data' &&
          freeTextValue.length > 1) ||
        (question.style === QUESTION_NUMERICAL_OPEN_ENDED &&
          freeTextNumericalValue.length > 0) ||
        question.style === QUESTION_RANKING ||
        question.style === QUESTION_MATRIX_SINGLE ||
        question.style === QUESTION_MATRIX_MULTIPLE ||
        question.style === QUESTION_MATRIX_VAN_WESTENDORP
      ) {
        if (
          question.style === QUESTION_RANKING ||
          question.style === QUESTION_MATRIX_SINGLE ||
          question.style === QUESTION_MATRIX_MULTIPLE ||
          question.style === QUESTION_MATRIX_VAN_WESTENDORP
        ) {
          each($('.answer'), (val) => {
            if (!$(val).hasClass('answerClick')) {
              $(val).tap();
            }
          });
        } else {
          $($('.answer')[0]).tap();
        }
        // select all the answers to proceed with validation
      } else if (
        question.style === QUESTION_MATRIX_OPEN_ENDED ||
        question.style === QUESTION_MATRIX_OPEN_ENDED_NUMERIC ||
        question.style === QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM
      ) {
        each($('.answer--matrix-open-ended'), (val) => {
          if (!$(val).hasClass('answerClick')) {
            $(val).addClass('answerClick');
          }
        });
      } else if (
        question.style === QUESTION_OPEN_ENDED &&
        question.f_name === 'dg_postal_data'
      ) {
        const selectedPostalCode = Pollfish.helper.getPostalCodeSelection();
        if (selectedPostalCode.length) {
          $($('.answer')[0]).tap();
        }
      } else if (
        question.style === QUESTION_SINGLE &&
        question.f_name === 'dg_year_of_birth'
      ) {
        if (freeTextNumericalValue.length === 4) {
          if (
            freeTextNumericalValue > question.age.max ||
            freeTextNumericalValue < question.age.min
          ) {
            return false;
          }

          each($('.answer'), (val) => {
            if ($.trim($(val).text()) === freeTextNumericalValue) {
              $(val).tap();
            }
          });

          if (
            freeTextNumericalValue >= question.answers[0].body.split('> ')[1]
          ) {
            $('.answer')
              .first()
              .tap();
          } else if (
            freeTextNumericalValue <=
            question.answers[question.answers.length - 1].body.split('< ')[1]
          ) {
            $('.answer')
              .last()
              .tap();
          }
        }
      }

      // event binded to solve ios issue with focus on textarea
      $('#wrapper').unbind('click');
      return true;
    },

    // prompt user for input
    promptMissingAnswer(question) {
      if (
        question.style === QUESTION_SINGLE ||
        question.style === QUESTION_NPS
      ) {
        if (question.f_name === 'dg_year_of_birth') {
          const val = Pollfish.helper.getNumericValue();
          Pollfish.analytics.sendEvent('targeting', 'invalidAge', val);
          Pollfish.ui.prompt(Pollfish.ui.getText('warnings.invalid_age'));
        } else {
          Pollfish.ui.prompt(Pollfish.ui.getText('warnings.single'));
        }
      } else if (question.style === QUESTION_MULTIPLE) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.multiple'));
      } else if (question.style === QUESTION_RATING) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.rating'));
      } else if (question.style === QUESTION_SLIDER) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.slider'));
      } else if (question.style === QUESTION_OPEN_ENDED) {
        if (question.f_name === 'dg_postal_data') {
          const postalCode = Pollfish.helper.getPostalCodeValue();
          if (postalCode.length < MINIMUM_POSTAL_CODE_LENGTH) {
            Pollfish.ui.prompt(Pollfish.ui.getText('warnings.open_ended'));
          } else {
            Pollfish.ui.prompt(Pollfish.ui.getText('warnings.single'));
          }
        } else {
          Pollfish.ui.prompt(Pollfish.ui.getText('warnings.open_ended'));
        }
      } else if (question.style === QUESTION_NUMERICAL_OPEN_ENDED) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.numeric_open'));
      } else if (question.style === QUESTION_NUMERICAL_OPEN_ENDED) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.numeric_open'));
      } else if (question.style === QUESTION_MATRIX_MAXDIFF) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.single'));
      } else if (question.style === QUESTION_MATRIX_BIPOLAR) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.matrix_bipolar'));
      } else if (question.style === QUESTION_DRILLDOWN) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.multiple'));
      }
    },

    // load template of question or the thank you screen if no question exists
    loadQuestionOrThankScreen() {
      // retrieve question
      let question;
      const questionElement = $('.js-question');
      const currentQuestionRefId = questionElement.data('ref-id');
      const currentQuestionId = questionElement.attr('id');
      // we prefer using refIds over ids but in some cases where a question might not
      // have a refId (ie: tag_questions) we need to fallback
      if (currentQuestionRefId) {
        question = Pollfish.survey.getQuestionByRefId(currentQuestionRefId);
      } else {
        question = Pollfish.survey.getQuestionById(currentQuestionId);
        const isProfilingQuestion = get(question, 'isProfilingQuestion', false);
        // throw a warning only if question is not a profiling one
        // because profiling questions does not contain a refId when comes for backend
        if (!isProfilingQuestion) {
          Raven.captureMessage(
            'Fallbacked to questionId when it should find a question by refId',
            {
              extra: {
                currentQuestionRefId,
                question,
                ...Pollfish.helper.getRedirectData(),
              },
            },
          );
        }
      }
      // remove padding bottom class
      $(window).off('resize');
      $('.wrapper__holder').removeClass('open-keyboard');
      $('#promptInfo').removeClass('prompt-info--visible');

      // check if video exists and user has seen the full video
      // in order to qualify for next question
      if (!Pollfish.ui.hasUserSeenAllVideos(question)) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.video'));
        return;
      }
      // check if video/audio exists and user has seen/heard the full video/audio
      // in order to qualify for next question (AB Testing)
      if (!isEmpty(question.selectedAttributes)) {
        const { groups } = Pollfish.survey;
        let group;
        let selectedAttributes;
        let referenceAttribute;
        if (!isEmpty(groups)) {
          group = groups.find(({ refId }) => question.groupRefId === refId);
        }
        if (!isEmpty(group)) {
          selectedAttributes = group.getAttributesForQuestion(question.refId);
        }
        if (!isEmpty(selectedAttributes)) {
          referenceAttribute = selectedAttributes.find(
            ({ media }) =>
              media && INTERACTIVE_MEDIA_TYPES.includes(media.type),
          );
        }
        if (referenceAttribute && !Pollfish.ui.hasUserSeenAllVideos(question)) {
          Pollfish.ui.prompt(Pollfish.ui.getText('warnings.video'));
          return;
        }
      }

      // apply virtual taps for question with different interface
      // than single and multiple. Effectively means "add .answerClick classes
      // where needed so that the next for loop will work as expected :P"
      Pollfish.ui.virtualTaps(question);

      const selectedAnswerIds = [];
      const selectedAnswers = [];

      for (let i = 0; i < $('.answerClick').length; i += 1) {
        const element = $('.answerClick')[i];
        const answerId = $(element).attr('id');

        selectedAnswerIds.push(answerId);
        selectedAnswers.push(Pollfish.survey.getAnswerById(answerId, question));
        // if question type is not multiple always break and don't allow more selected answers
        // this is as a fallback to make sure things don't break
        // TODO: remove this and handle it differently
        if (
          question.style !== QUESTION_MULTIPLE &&
          question.style !== QUESTION_RANKING &&
          question.style !== QUESTION_MATRIX_SINGLE &&
          question.style !== QUESTION_MATRIX_MULTIPLE &&
          question.style !== QUESTION_MATRIX_VAN_WESTENDORP &&
          question.style !== QUESTION_MATRIX_MAXDIFF &&
          question.style !== QUESTION_MATRIX_BIPOLAR &&
          question.style !== QUESTION_MATRIX_OPEN_ENDED &&
          question.style !== QUESTION_MATRIX_OPEN_ENDED_NUMERIC &&
          question.style !== QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM
        ) {
          break;
        }
      }

      // if no answer is selected don't qualify to next question
      // and prompt user for input
      if (isEmpty(selectedAnswers)) {
        // prompt user for input
        Pollfish.ui.promptMissingAnswer(question);

        return;
      }

      const answerErrors = Survey.validateAnswers(selectedAnswers);
      if (answerErrors.emptyOtherAnswer) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.open_ended'));
        return;
      }
      // TODO: move question validations per question type outside ui file
      if (!Pollfish.ui.isQuestionValid(question, selectedAnswers)) {
        return;
      }

      // since the answer is qualifying
      $('body').removeAttr('data-f_name');

      // we only destroy the slider after validating.
      // Or else the user won't be able to answer again
      invoke(Pollfish, 'ui.slider.destroy');
      // we only destroy the bipolar after validating.
      // Or else the user won't be able to answer again.
      invoke(Pollfish, 'ui.bipolar.destroy');

      if (
        !Pollfish.helper.hasPassingAnswer(
          question,
          selectedAnswers,
          selectedAnswerIds,
        )
      ) {
        if (question.isTag) {
          Pollfish.demographics.setDisqualified(
            true,
            question,
            selectedAnswers,
          );
        } else if (question.isProfilingQuestion) {
          Pollfish.profiling.setDisqualified(true, question, selectedAnswers);
        } else if (question.isSanityQuestion) {
          Pollfish.sanity.setDisqualified(true);
        } else {
          Pollfish.survey.setDisqualified(true);
        }
      }

      Pollfish.survey.saveAnswer(question, selectedAnswers);

      // make the demographics check only once
      const isLastDemographic =
        question.isTag && Pollfish.demographics.isCompleted();
      const checkSurveyStatusAfterLastDemographic =
        isLastDemographic && Pollfish.profiling.isCompleted();
      // make the profiling check only once
      const isLastProfiling =
        question.isProfilingQuestion && Pollfish.profiling.isCompleted();
      const checkSurveyStatusAfterLastProfiling =
        isLastProfiling && Pollfish.demographics.isCompleted();
      // check once if user has completed demographics and profiling questions
      const userHasCompletedDemographicsAndProfiling =
        checkSurveyStatusAfterLastDemographic ||
        checkSurveyStatusAfterLastProfiling;
      let isUserNotEligible =
        Pollfish.demographics.isDisqualified() ||
        Pollfish.profiling.isDisqualified();
      const isUserUnderage =
        Pollfish.demographics.getDisqualificationReason() ===
        DISQUALIFICATION_USER_UNDERAGE;
      // immediately finish if user is underage
      if (isUserNotEligible && isUserUnderage) {
        Pollfish.ui.getNextQuestionOrFinish(
          question,
          selectedAnswers,
          selectedAnswerIds,
        );
        return;
      }
      isUserNotEligible =
        Pollfish.demographics.isDisqualified() ||
        Pollfish.profiling.isDisqualified();
      // demographic surveys should not check survey status
      if (
        userHasCompletedDemographicsAndProfiling &&
        !Pollfish.survey.isDemographicSurvey()
      ) {
        checkAndEnterSurvey({
          tagAnswers: Pollfish.demographics.getStoredAnswers(),
          profilingAnswers: Pollfish.profiling.getStoredAnswers(),
          isUserNotEligible,
          onSurveyContinue: () => {
            // Only disqualify the respondent when all tag and profiling
            // questions have been answered
            if (!isUserNotEligible) {
              // We need to change the language to the survey language now...
              const userLang = Pollfish.helper.json.user_lang;
              const surveyLang = Pollfish.helper.json.lang;
              if (surveyLang && userLang !== surveyLang) {
                Pollfish.helper.changeLanguage(surveyLang);
              }
            }
            Pollfish.ui.getNextQuestionOrFinish(
              question,
              selectedAnswers,
              selectedAnswerIds,
            );
          },
        });
        return;
      }
      Pollfish.ui.getNextQuestionOrFinish(
        question,
        selectedAnswers,
        selectedAnswerIds,
      );
    },

    getNextQuestionOrFinish(question, selectedAnswers, selectedAnswerIds) {
      const isUserNotEligible = Pollfish.helper.isUserNotEligible(
        question,
        selectedAnswers,
        selectedAnswerIds,
      );
      Pollfish.survey
        .getNextQuestion(selectedAnswers, question)
        .then((nextQuestion) => {
          let shouldOverrideNextQuestion = false;
          // we need to allow tagQuestions and profilingQuestions to finish before disqualifying
          // but not if a tagQuestion should disqualify immediately
          const nextQuestionIsNotAllowed =
            !question.isTag && !question.isProfilingQuestion;
          if (
            isUserNotEligible &&
            (nextQuestionIsNotAllowed ||
              Pollfish.demographics.shouldDisqualifyImmediately())
          ) {
            shouldOverrideNextQuestion = true;
          }

          if (nextQuestion && !shouldOverrideNextQuestion) {
            // transfer user to the top to check new question
            Pollfish.ui.moveToQuestion(nextQuestion);
            return;
          }

          if (isUserNotEligible) {
            Pollfish.helper.handleUserNotEligible(question);
            return;
          }
          if (Pollfish.helper.is3rdParty()) {
            Pollfish.helper.redirectTo3rdParty();
            return;
          }
          if (Pollfish.survey.isFeedbackSurvey()) {
            Pollfish.helper.handleSurveyReject();
            return;
          }

          Pollfish.surveyState.setState(SURVEY_STATE.COMPLETING_SURVEY);
          Pollfish.helper
            .handleSurveyComplete({
              isUserNotEligible: false,
            })
            .catch(Pollfish.helper.handleGlobalPromiseError);
        })
        .catch(Pollfish.helper.handleUnsuccesfullGroupSetup);
    },

    // transfer user to the top to check new question
    moveToQuestion(nextQuestion) {
      // $('#wrapper').off('tap', '#submit-btn');
      $('#wrapper').off('tap', '.answer');
      $('#wrapper').off('tap', '.rating-stars__star');
      $('#wrapper').off('tap', '.matrix__answer');
      $('#wrapper').off('tap', '.matrix__question');
      $('#wrapper').off('tap', '.matrix__hide-answers-btn');
      $('#wrapper').off('tap', '.ranking__answer-btn');
      $('#wrapper').off('input', '#postalSearch');
      $('#wrapper').off(
        'tap',
        '.country-open .country-code, .ignore-collapse .country-code',
      );
      $('#wrapper').off('tap', '.country-header');
      $('#wrapper').off('keyup', '.input-van-westendorp');
      $('#wrapper').off('blur', '.input-van-westendorp');

      Pollfish.event.clearInteractions();
      $('#freeText').off();
      // not sure if mount/unmount is always a good idea but i don't know what kind of side effects
      // will exist when jQuery also affects the same root
      Pollfish.ui.unmountReactRoot();

      setTimeout(() => {
        $('#wrapper').scrollTop(0);
        $('html').scrollTop(0);
      }, 10);

      Pollfish.ui.applyTemplate(nextQuestion);
    },

    // intermediate screen for loading purposes
    loading() {
      Pollfish.analytics.sendEvent('general', 'router', 'Router');
      const wrapper = $('html');

      wrapper.animate({ scrollTop: 0 }, 10, () => {
        const content = FetchAdditionalSurveysScreen({
          lastPayload: Pollfish.store.lastPayload,
        });
        Pollfish.ui.applyMainContentTemplate(content);
        $('body').attr('data-page', 'loading');
      });
    },

    getSpinner(props) {
      return Loading(props);
    },

    showOverlaySpinner() {
      const content = Loading();
      const wrapper = $('#overlay-spinner');
      wrapper.addClass('open');
      wrapper.html(content);
    },

    hideOverlaySpinner() {
      setTimeout(() => {
        const wrapper = $('#overlay-spinner');
        wrapper.removeClass('open');
        wrapper.html('');
      }, 0);
    },

    showSpinner(texts) {
      const wrapper = $('html');
      const scrollDuration = Pollfish.helper.isCypressEnv() ? 0 : 10;
      // NOTE: this is buggy. If anything renders with a delay less than 10ms
      // this will overwrite everything. That is why we return a promise, to indicate
      // when an action can actually happen
      return new Promise((resolve) => {
        wrapper.animate({ scrollTop: 0 }, scrollDuration, () => {
          const content = Loading(texts);
          Pollfish.ui.applyMainContentTemplate(content);
          $('body').attr('data-page', 'loading');
          resolve();
        });
      });
    },

    showSurveyLoading() {
      const subtitle = Pollfish.ui.getText('offerwall.redirect.loading');
      return Pollfish.ui.showSpinner({ subtitle });
    },

    thirdPartyLoader() {
      const wrapper = $('body');
      wrapper.html('');
      wrapper.attr('data-page', 'loading');
    },

    show3rdPartySurvey() {
      const url = Pollfish.survey.getRedirectURL();
      // if no webview is available, just open the website in a browser
      if (!Pollfish.helper.opensInWebview()) {
        Pollfish.mobile.interface.openWebsite(url);
        return;
      }

      // MOBILE SDK Only [PUB-165]
      const providerName = Pollfish.helper.json.third_party_provider;
      const baseURL = Pollfish.helper.getBaseRequestURL();
      const redirectData = Pollfish.helper.getRedirectData();
      const redirectURL = `${baseURL}/v2/device/register/true?dontencrypt=true&json=${encodeURIComponent(
        JSON.stringify(redirectData),
      )}`;
      const prompt = Pollfish.ui.getText('offerwall.leaveSurvey.prompt');
      const title = Pollfish.ui.getText('offerwall.leaveSurvey.title');
      const subtitle = Pollfish.ui.getText('offerwall.leaveSurvey.subtitle');
      const textContent = `${title}\n${subtitle}`;
      const yesText = Pollfish.ui.getText('yes') || 'Yes';
      const noText = Pollfish.ui.getText('no') || 'No';
      const action = {
        action: 'redirect',
        redirectURL,
        actionTitle: prompt,
        actionDescription: textContent,
        actionConfirm: yesText,
        actionCancel: noText,
      };
      Pollfish.mobile.interface.openWebsiteInWebview(
        url,
        Pollfish.ui.getText('surveyBy'),
        providerName,
        action,
      );
    },

    // show thank you component on basic format
    thankYouBasic({
      isResultPending,
      reward,
      payload,
      surveyId,
      onDemandEndPageUrl,
      payloadIsFromLastPayload,
      isLastPayloadFromDemographicSurvey,
      forOffers = false,
      forSurveys = false,
    } = {}) {
      if (forOffers || forSurveys) {
        const thankYou = new ThankYouPage({
          isResultPending,
          reward,
          surveyId,
          redirectOnDemand,
          endPageSettingType,
          onDemandEndPageUrl,
          rewardIsFromLastPayload: payloadIsFromLastPayload,
          rewardIsFromLastPayloadDemographicSurvey: isLastPayloadFromDemographicSurvey,
          recruitmentCampaignName,
          recruitmentOfferToken,
          forOffers,
          forSurveys,
        }).render();
        const container = forOffers
          ? `.${OFFERS_CONTAINER_CLASS_NAME}`
          : `.${SURVEYS_CONTAINER_CLASS_NAME}`;
        $(container).html(thankYou);
        return;
      }

      // we need to clear the last payload since it will be used in this view
      Pollfish.store.lastPayload = undefined;

      let shouldRedirectToCustomEndPage = Boolean(
        payload &&
          payload.customEndPage &&
          surveyId &&
          !payload.redirectOnDemand,
      );
      // Should not redirect to customEndPage even if exists
      // when survey is a demographic survey on offerwall integration
      // it could be better if backend decide if we should redirect to a customEndPage
      if (
        Pollfish.helper.isOfferwall() &&
        Pollfish.survey &&
        Pollfish.survey.isDemographicSurvey()
      ) {
        shouldRedirectToCustomEndPage = false;
      }
      if (shouldRedirectToCustomEndPage) {
        const redirectURL = Pollfish.helper.getRedirectURL(surveyId);
        if (redirectURL) {
          window.location.href = redirectURL;
          return;
        }
      }
      const wrapper = $('html');
      const currentPage = $('body').attr('data-page');
      let container = $('.main-content');
      if (
        Pollfish.helper.isOfferwall() &&
        (currentPage === 'unlock-screen' || Pollfish.helper.noSurveyAvailable)
      ) {
        const offerWallContainer = $(`.${OFFERS_CONTAINER_CLASS_NAME}`);

        if (offerWallContainer.length <= 0) {
          Pollfish.analytics.sendEvent(
            'error',
            'offerwall-container-not-existing',
            container,
          );
        } else {
          container = offerWallContainer;
        }
      }

      Raven.captureBreadcrumb({
        message: 'thankYouBasic',
        category: 'thankYouBasic',
      });

      // these settings are currently only expected on distributionLink surveys
      const redirectOnDemand = get(payload, 'redirectOnDemand', false);
      const endPageSettingType = get(payload, 'endPageSettingType');

      // these settings are currently only expected when we need to show a recruitment banner
      let recruitmentCampaignName = get(payload, 'recruitmentCampaignName');
      let recruitmentOfferToken = get(payload, 'recruitmentOfferToken');

      // if recruitment campaign name did not found in available app name
      // then do not show a recruitment banner
      if (
        recruitmentCampaignName &&
        AVAILABLE_RECRUITMENT_CAMPAIGNS.indexOf(recruitmentCampaignName) === -1
      ) {
        Raven.captureMessage(
          'Received an recruitment campaign name which is not available',
          {
            extra: {
              recruitmentCampaignName,
              recruitmentOfferToken,
              payload: JSON.stringify(payload),
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        recruitmentCampaignName = null;
        recruitmentOfferToken = null;
      }

      const thankYou = new ThankYouPage({
        isResultPending,
        reward,
        surveyId,
        redirectOnDemand,
        endPageSettingType,
        onDemandEndPageUrl,
        rewardIsFromLastPayload: payloadIsFromLastPayload,
        rewardIsFromLastPayloadDemographicSurvey: isLastPayloadFromDemographicSurvey,
        recruitmentCampaignName,
        recruitmentOfferToken,
      }).render();
      container.html(thankYou);

      // deferring scrolling until the render of the thank you page has completed
      // PUB-1328
      setTimeout(() => {
        wrapper.scrollTop(0);
      }, 10);

      if (Pollfish.helper.isOfferwall() && Pollfish.helper.noSurveyAvailable) {
        $('body').attr('data-page', 'no-survey-available--offerwall');
      } else {
        $('body').attr('data-page', 'thank-you');
      }
    },

    showOfferwall(page) {
      const wrapper = $('html');

      Raven.captureBreadcrumb({
        message: 'OfferwallHomescreen',
        category: 'OfferwallHomescreen',
      });

      // will reset offerwall html otherwise
      wrapper
        .animate({ scrollTop: 0 }, 10)
        .promise()
        .done(() => {
          InitOfferwall(page);
        });
    },

    showUnlockScreen() {
      $('body').attr('data-page', 'unlock-screen');
      const unlockScreenContent = new UnlockScreen().render();
      return unlockScreenContent;
    },

    showFeedbackSurvey() {
      Raven.captureBreadcrumb({
        message: 'feedbackSurvey',
        category: 'feedbackSurvey',
      });
      const originalFeedbackQuestion = get(
        Pollfish,
        'helper.feedbackQuestions[0]',
      );
      const currentPage = $('body').attr('data-page');
      const feedbackQuestion = {
        ...originalFeedbackQuestion,
        answers: [...originalFeedbackQuestion.answers],
      };

      // answer other on feedback does not have label, so was rendering undefined on screen
      const { answers } = feedbackQuestion;
      const lastAnswer = answers[answers.length - 1];
      if (lastAnswer.style === ANSWER_OTHER && !lastAnswer.label) {
        lastAnswer.label = 'Please specify';
      }

      if (currentPage.match(/(wait-screen)/)) {
        Pollfish.store.userRejectedSurveyOnWaitScreen = true;
        Pollfish.helper.waitPage.clearTimer();
        // transform single choice question to open ended
        if (lastAnswer.style === ANSWER_OTHER) {
          feedbackQuestion.style = QUESTION_OPEN_ENDED;
          feedbackQuestion.answers = [lastAnswer];
        }
      }

      const isCurrentSurveyDemographicsOnOfferwall = get(
        Pollfish,
        'survey.props.isOfferwallDemographics',
      );
      if (!Pollfish.helper.hasCustomSurveyTheme()) {
        // maybe pass this on to the backend
        feedbackQuestion.image_url = `${publicAssetsPrefix}/img/feedback.jpg`;
      }

      let surveySessionId;
      if (Pollfish.survey) {
        surveySessionId = Pollfish.survey.getSurveySessionId();
      }

      Pollfish.survey = new Survey(
        {
          ...feedbackSurvey,
          isFeedbackSurvey: true,
          isOfferwallDemographics: isCurrentSurveyDemographicsOnOfferwall,
          s_id: Pollfish.helper.json.s_id,
          survey_session_id: surveySessionId,
          questions: [feedbackQuestion],
          third_party_provider: Pollfish.helper.json.third_party_provider,
          no_check: invoke(Pollfish, 'survey.getNoCheck'),
        },
        {
          checkStatus: false,
        },
      );
      Pollfish.survey.start().catch(Pollfish.helper.handleError);
    },

    showSurveyExpiredScreen(nextScreenCallback) {
      const wrapper = $('html');
      wrapper.animate({ scrollTop: 0 }, 10, () => {
        const content = FetchAdditionalSurveysScreen({
          surveyExpired: true,
          callback: nextScreenCallback,
        });

        Pollfish.ui.applyMainContentTemplate(content);
        $('body').attr('data-page', 'loading');
      });
    },

    showWaitingScreen() {
      const wrapper = $('html');

      Raven.captureBreadcrumb({
        message: 'waitScreen',
        category: 'waitScreen',
      });

      const BASE_COUNTDOWN = 2;
      wrapper.animate({ scrollTop: 0 }, 10, () => {
        if (Pollfish.helper.waitPage) {
          // two timers might be active at the same time
          Pollfish.helper.waitPage.clearTimer();
        }
        const waitPage = new WaitScreen({
          countdownSeconds:
            BASE_COUNTDOWN + Pollfish.store.sanityScreenouts * 4,
          onComplete: Pollfish.survey.reset,
        });
        Pollfish.helper.waitPage = waitPage;
        const htmlTemplate = waitPage.render();

        Pollfish.ui.applyMainContentTemplate(htmlTemplate);
        waitPage.startCountdown();
        // deferring scrolling until the render of the thank you page has completed
        setTimeout(() => {
          wrapper.scrollTop(0);
          // Pollfish.mobile.interface.userNotEligible();
        }, 10);

        $('body').attr('data-page', 'wait-screen');
      });
    },

    showScreenOut({
      reason,
      reward,
      payload,
      surveyId,
      onDemandEndPageUrl,
    } = {}) {
      // we need to clear the last payload since it will be used in this view
      Pollfish.store.lastPayload = undefined;
      const wrapper = $('html');

      const shouldRedirectToCustomEndPage = Boolean(
        payload &&
          payload.customEndPage &&
          surveyId &&
          !payload.redirectOnDemand,
      );

      if (shouldRedirectToCustomEndPage) {
        const redirectURL = Pollfish.helper.getRedirectURL(surveyId);
        if (redirectURL) {
          Pollfish.mobile.interface.userNotEligible(payload);
          window.location.href = redirectURL;
          return;
        }
      }

      Raven.captureBreadcrumb({
        message: 'screenOutPage',
        category: 'screenOutPage',
      });
      const redirectOnDemand = get(payload, 'redirectOnDemand', false);
      const endPageSettingType =
        payload && redirectOnDemand && payload.endPageSettingType;

      // these settings are currently only expected when we need to show a recruitment banner
      let recruitmentCampaignName = get(payload, 'recruitmentCampaignName');
      let recruitmentOfferToken = get(payload, 'recruitmentOfferToken');

      // if recruitment campaign name did not found in available app name
      // then do not show a recruitment banner
      if (
        recruitmentCampaignName &&
        AVAILABLE_RECRUITMENT_CAMPAIGNS.indexOf(recruitmentCampaignName) === -1
      ) {
        Raven.captureMessage(
          'Received an recruitment campaign name which is not available',
          {
            extra: {
              recruitmentCampaignName,
              recruitmentOfferToken,
              payload: JSON.stringify(payload),
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        recruitmentCampaignName = null;
        recruitmentOfferToken = null;
      }

      wrapper.animate({ scrollTop: 0 }, 10, () => {
        const htmlTemplate = new ScreenOutPage({
          reason,
          reward,
          redirectOnDemand,
          endPageSettingType,
          onDemandEndPageUrl,
          recruitmentCampaignName,
          recruitmentOfferToken,
        }).render();

        Pollfish.ui.applyMainContentTemplate(htmlTemplate);

        // deferring scrolling until the render of the thank you page has completed
        // PUB-1328
        setTimeout(() => {
          wrapper.scrollTop(0);
          Pollfish.mobile.interface.userNotEligible(payload);
        }, 10);

        $('body').attr('data-page', 'thank-you');
      });
    },

    changeVideoVolume(videoRefId) {
      const video = document.getElementById(`video-${videoRefId}`);
      const customControls = document.getElementById(
        `questionVideo-${videoRefId}-controls`,
      );
      const volumeBar = customControls.querySelector(
        `#volume-bar-${videoRefId}`,
      );
      video.volume = volumeBar.value;
      const volumeButton = customControls.querySelector(
        `#volume-button-${videoRefId}`,
      );
      if (volumeBar.value === '0') {
        volumeButton.innerHTML = muteIcon;
      } else {
        volumeButton.innerHTML = volumeIcon;
      }
    },

    handleVolumeInputChange(e) {
      const { min, max, value } = e.target;

      // eslint-disable-next-line
      const width = ((value - min) * 100) / (max - min);
      e.target.style.backgroundSize = `${width}% 100%`;
    },

    toggleVolume(videoRefId) {
      const video = document.getElementById(`video-${videoRefId}`);
      const customControls = document.getElementById(
        `questionVideo-${videoRefId}-controls`,
      );
      const volumeBar = customControls.querySelector(
        `#volume-bar-${videoRefId}`,
      );
      const volumeButton = customControls.querySelector(
        `#volume-button-${videoRefId}`,
      );
      if (video.volume > 0) {
        video.volume = 0;
        volumeBar.value = 0;
        volumeButton.innerHTML = muteIcon;
        volumeBar.style.backgroundSize = '0% 100%';
      } else {
        video.volume = 0.5;
        volumeBar.value = 0.5;
        volumeButton.innerHTML = volumeIcon;
        volumeBar.style.backgroundSize = '50% 100%';
      }
    },

    // handle video element after user clicked
    handleVideo(videoRefId, disableControl) {
      // get video element
      const video = document.getElementById(`video-${videoRefId}`);

      $(video)
        .siblings('.video-overlay-play-button')
        .hide();

      // start playing video
      video.play();

      // enable controls for the user to pause the video
      if (!disableControl) {
        $(video).attr('controls', true);
      } else {
        const container = $(`#questionVideo-${videoRefId}-controls`);
        container.addClass('visible');
        const volumeInput = container
          .get(0)
          .querySelector('input[type="range"]');
        volumeInput.addEventListener('input', this.handleVolumeInputChange);
        const volumeButton = document.getElementById(
          `volume-button-${videoRefId}`,
        );
        volumeButton.addEventListener('click', () =>
          this.toggleVolume(videoRefId),
        );
      }

      // Hide full screen btn when device is android and has sdk version lower than 17
      Pollfish.ui.hideFullScreenBtn(video, 17);

      // attach listener to be notified if user watched the video/audio
      video.addEventListener('play', Pollfish.ui.onVideoPlay, false);

      // attach listener to be notified if user watched the video/audio
      video.addEventListener('ended', Pollfish.ui.onVideoEnd, false);

      // attach listener to be notified about video time update
      // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/timeupdate_event
      video.addEventListener(
        'timeupdate',
        Pollfish.ui.onVideoTimeUpdate,
        false,
      );
    },

    hideFullScreenBtn(video, sdkVersion) {
      return Pollfish.helper.json.sdk_version < sdkVersion &&
        Pollfish.helper.isAndroid()
        ? $(video).addClass('full-screen-btn--hidden')
        : '';
    },

    // when a video/audio start pause all the other video/audio
    onVideoPlay(event) {
      const videoRefId = $(event.target).data('video-ref-id');
      const questionRefId = $(event.target).data('question-ref-id');
      if (!isEmpty(Pollfish.store.questionVideos)) {
        let videoRefIds = Pollfish.store.questionVideos.filter(
          ({ refId, questionRefIds }) => {
            if (
              refId !== videoRefId &&
              questionRefIds.includes(questionRefId)
            ) {
              return true;
            }
            return false;
          },
        );
        videoRefIds = videoRefIds.map(({ refId }) => refId);
        videoRefIds.forEach((videoId) => {
          const video = document.getElementById(`video-${videoId}`);
          if (video) {
            video.pause();
          }
        });
      } else {
        Raven.captureException(
          'Found a video/audio playing without existing in store',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
      }
    },

    onVideoTimeUpdate(event) {
      const video = event.target;
      const videoRefId = $(video).attr('data-video-ref-id');
      const currentTime = parseInt(video.currentTime, 10);

      if (isNaN(currentTime)) {
        Raven.captureException(
          'timeUpdate found a video/audio with currentTime as NaN',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return;
      }

      // user may seeking to go forward or backwards,
      // when seeking the video is paused so we should not make any update
      if (video.paused) {
        return;
      }

      if (isEmpty(Pollfish.store.questionVideos)) {
        Raven.captureException(
          'timeUpdate found a video/audio without videos in store',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return;
      }

      const storedVideoIndex = Pollfish.store.questionVideos.findIndex(
        (videoInStore) => videoInStore.refId === videoRefId,
      );

      if (storedVideoIndex < 0) {
        Raven.captureException(
          'timeUpdate found a video/audio without video index existing in store',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return;
      }
      const customControls = document.getElementById(
        `questionVideo-${videoRefId}-controls`,
      );
      if (customControls) {
        const progressBar = customControls.querySelector('.video-progress-bar');
        // Work out how much of the media has played via the duration and currentTime parameters
        const percentage = Math.floor((100 / video.duration) * currentTime);

        // Update the progress bar's value
        progressBar.value = percentage;
        // Update the progress bar's text (for browsers that don't support the progress element)
        progressBar.innerHTML = `${percentage}% played`;
      }

      if (Pollfish.store.questionVideos[storedVideoIndex].seen) {
        return;
      }

      Pollfish.store.questionVideos[storedVideoIndex].secondsSeen[
        currentTime
      ] = 1;
    },

    // check if video/audio has ended
    onVideoEnd(event) {
      const videoRefId = $(event.target).attr('data-video-ref-id');
      Pollfish.mobile.interface.notifyVideoEnd();

      const customControls = document.getElementById(
        `questionVideo-${videoRefId}-controls`,
      );
      if (customControls) {
        const progressBar = customControls.querySelector('.video-progress-bar');
        progressBar.value = 100;
        // Update the progress bar's text (for browsers that don't support the progress element)
        progressBar.innerHTML = '100% played';
      }

      if (
        !Pollfish.ui.hasUserAlreadySeenVideo(videoRefId) &&
        !Pollfish.ui.hasUserSeenFullVideo(videoRefId)
      ) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.video'));
        return;
      }
      if (!isEmpty(Pollfish.store.questionVideos)) {
        Pollfish.store.questionVideos = map(
          Pollfish.store.questionVideos,
          (video) => {
            if (video.refId === videoRefId) {
              return { ...video, seen: true, secondsSeen: [] };
            }
            return video;
          },
        );
      } else {
        Raven.captureException(
          'Found a video/audio ended without existing in store',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
      }
    },

    // we check the array of secondsSeen of a stored video
    // and if the summary of the seconds is equal minus one second
    // to the video duration means user has seen full video
    hasUserSeenFullVideo(videoRefId) {
      const video = document.getElementById(`video-${videoRefId}`);
      const videoDuration = video.duration;

      if (isEmpty(Pollfish.store.questionVideos)) {
        Raven.captureException(
          'hasUserSeenFullVideo found a video/audio without existing in store',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return false;
      }

      const storedVideo = Pollfish.store.questionVideos.find(
        (videoInStore) => videoInStore.refId === videoRefId,
      );

      if (!storedVideo) {
        Raven.captureException(
          'hasUserSeenFullVideo found a video/audio index without existing in store',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return false;
      }
      const storedVideoDuration = storedVideo.secondsSeen
        .filter((second) => !!second)
        .reduce((a, b) => a + b, 0);
      const diffInSeconds = Math.abs(storedVideoDuration - videoDuration);
      return diffInSeconds <= 1;
    },

    hasUserSeenAllVideos(question) {
      if (!question.video && isEmpty(question.conceptVideos)) {
        return true;
      }
      const questionInteractiveMedia = Pollfish.store.questionVideos.filter(
        (video) => video.questionRefIds.includes(question.refId),
      );
      if (isEmpty(questionInteractiveMedia)) {
        throw new Error(
          'Expected to find video media for question but found nothing',
        );
      }
      return questionInteractiveMedia.every((video) => video.seen === true);
    },

    hasUserAlreadySeenVideo(videoRefId) {
      const video = find(Pollfish.store.questionVideos, ['refId', videoRefId]);
      if (video) {
        return video.seen;
      }
      return false;
    },

    // display terms
    displayTerms({ showCustomTerms = false } = {}) {
      let termsUrl =
        'https://api.github.com/repos/pollfish/docs/contents/terms/respondent.md';

      if (Pollfish.helper.isDistributionLink()) {
        termsUrl =
          'https://api.github.com/repos/pollfish/docs/contents/terms/respondent-link.md';
      }

      $.ajax({
        url: termsUrl,
        headers: { Accept: 'application/vnd.github.VERSION.raw' },
      }).done((response) => {
        $('.js-github-terms').html(response.toString().wiki2html());
        $('html, body').scrollTop(0);
      });

      if (showCustomTerms) {
        const surveyId = Pollfish.helper.json.s_id;
        request({
          url: `/v2/survey/${surveyId}/terms`,
          method: 'GET',
          transformData: false,
        })
          .then(({ title, body }) => {
            const template = `
            <h3 class="custom-terms-title">${$.trim(String(title || ''))}</h3>
            <div class="custom-terms-body">${$.trim(String(body || ''))}</div>
          `;
            $('.survey-terms-content').html(template);
            $('.terms-and-conditions').addClass('with-custom-terms');

            $('.custom-terms-link > a')
              .off('tap')
              .on('tap', (event) => {
                event.preventDefault();
                const newPosition = $('#survey-terms').position().top;
                $('html, body').animate({ scrollTop: newPosition }, 100);
              });
          })
          .catch((e) => {
            if (e) {
              // eslint-disable-next-line
              console.error(e);
              Raven.captureException(e);
            }
            Pollfish.survey.showOfferwallHomescreen();
          });
      }
    },

    // display GDPR
    displayGDPR() {
      const container = $('.main-content');
      const dimensions = Pollfish.helper.getInitialHeight();
      // NOTE: adds overhead but helps not having to check it every timprogressBar-containere.
      $('body').removeClass('gpdr--has-enough-space');
      $('body').attr('data-page', 'gdpr');
      setTimeout(() => {
        $('html, body').scrollTop(0);
      }, 10);

      // checks if device is in portrait mode
      // NOTE: This check does not need to happen more than once since the project
      //       will re-initialize itself on device resize through the SDK
      if (dimensions.height < dimensions.width) {
        container.find('.gdpr-footer--inner').removeClass('hide');
        $('body').addClass('gpdr--has-enough-space');
        if (
          !$('.js-agree-to-gdpr').isOnScreen() &&
          Pollfish.helper.flag.showScrollDownMsg
        ) {
          Pollfish.helper.flag.showScrollDownMsg = false;
          Pollfish.ui.prompt(Pollfish.ui.getText('scroll_down'));
        }
      }
    },

    noSurveyAvailable({ forOffers = false, forSurveys = false } = {}) {
      Pollfish.helper.noSurveyAvailable = true;

      const { lastPayload } = Pollfish.store;
      const reward = Pollfish.helper.getRewardFromParticipation(lastPayload);

      if (
        Pollfish.helper.isDistributionLink() &&
        !Pollfish.helper.json.containsSurvey
      ) {
        Pollfish.ui.thankYouBasic();
        return;
      }

      if (forOffers || forSurveys) {
        Pollfish.ui.thankYouBasic({ forOffers, forSurveys });
        if (forSurveys) {
          Pollfish.helper.sendSessionFinishedToServer();
        }
        return;
      }

      if (lastPayload && lastPayload.status === 'success') {
        Pollfish.helper.sendSessionFinishedToServer();
        // being here mean that respondent has already completed previously a survey
        // after completion entered router but there has not additional survey
        // so we should show the thank you page. Also thank you page must know if
        // respondent sees the page after a router somehow to show the correct literals.
        // Also thank you page should know if the survey answered previously the demographic survey.
        const isLastPayloadFromDemographicSurvey =
          lastPayload.survey_class === 'Pollfish/Demographics';
        Pollfish.ui.thankYouBasic({
          isResultPending: false,
          reward,
          payload: lastPayload,
          payloadIsFromLastPayload: true,
          isLastPayloadFromDemographicSurvey,
          forSurveys,
          forOffers,
        });
        return;
      }
      if (!isEmpty(reward)) {
        Pollfish.ui.showScreenOut({ reward, payload: lastPayload });
      } else {
        // this can happen when we redirect from a third party with an empty content
        // and a lastSurveyId in the URL lastSurveyId gets replaced with null and sets
        // its value in the s_id property. see helper.js LOC 504
        const shouldCallNotEligible =
          Pollfish.helper.json.s_id || Pollfish.helper.json.containsSurvey;

        if (!Pollfish.helper.isOfferwall() && shouldCallNotEligible) {
          Pollfish.mobile.interface.userNotEligible();
        }

        // legacy code TBD in the future
        // this helps to close indicator permanently
        // since closeAndNoShow simply checks if the survey completed in SDK
        if (Pollfish.helper.json.sdk_version <= 5) {
          Pollfish.mobile.interface.setSurveyCompleted();
        }
      }

      Pollfish.helper.sendSessionFinishedToServer();
      const surveyId = get(lastPayload, 'surveyId');
      // The no survey available screen is defered because, in the case of an error
      // the function might get called together with some other rendering function.
      // Defering helps with keeping this function ontop (unless we also have defered
      // the conflicting render function, in which case... well...)
      setTimeout(() => {
        Pollfish.ui.thankYouBasic({
          payload: lastPayload,
          surveyId,
          forSurveys,
          forOffers,
        });
      }, 0);
    },

    hideCloseButtonFromHeader() {
      const elements = document.getElementsByClassName(
        'header__action-close-btn',
      );
      if (elements && elements.length > 0) {
        const element = elements[0];
        element.style.display = 'none';
      }
    },

    hideCloseButtonFromHomescreen() {
      Pollfish.helper.closeButtonHidden = true;
      const elements = document.getElementsByClassName(
        'header__action-close-btn',
      );
      if (elements && elements.length > 0) {
        const element = elements[0];
        element.style.display = 'none';
      }
    },

    showCloseButtonAtHeader() {
      Pollfish.helper.closeButtonHidden = false;
      const elements = document.getElementsByClassName(
        'header__action-close-btn',
      );
      if (elements && elements.length > 0) {
        const element = elements[0];
        element.style.display = 'block';
      }
    },

    hideLogo() {
      const logoElement = document.querySelector('.header__logo');
      if (logoElement) {
        logoElement.style.display = 'none';
      }
      const headerElement = document.querySelector(
        '.header .header__container',
      );
      if (headerElement) {
        headerElement.style.height = 'unset';
      }
    },

    // download images for questions
    downloadImages(questionIndex) {
      const question = Pollfish.helper.json.questions[questionIndex];
      const questionImageToShow =
        question.optimized_image_url || question.image_url;
      const hasImageToShow = Boolean(questionImageToShow);
      // download question image if it exists
      if (hasImageToShow) {
        const questionImage = new Image();
        questionImage.src = questionImageToShow;
      }

      // download answer images if they exist
      for (let i = question.answers.length - 1; i >= 0; i -= 1) {
        const answer = question.answers[i];
        const answerDefaultImage =
          answer.optimized_image_url || answer.image_url;
        if (answerDefaultImage) {
          const answerImage = new Image();
          answerImage.src = answerDefaultImage;
        }
      }
      // download question poster if video exists
      if (question.video) {
        const questionPoster = new Image();
        questionPoster.src = question.video.poster;
      }

      // download images only once the first question has been answered
      const hasCustomTheme = Pollfish.helper.hasCustomSurveyTheme();
      if (
        questionIndex > 0 &&
        !Pollfish.helper.hasDownloadImages &&
        !hasCustomTheme
      ) {
        Pollfish.ui.downloadPollfishImages();
      }
    },

    downloadPollfishImages() {
      const orcaImage = new Image();
      orcaImage.src = `${publicAssetsPrefix}/img/orca.png`;

      const searchImage = new Image();
      searchImage.src = `${publicAssetsPrefix}/img/search.png`;

      const thankYouImage = new Image();
      thankYouImage.src = `${publicAssetsPrefix}/img/thankyou.png`;

      const qualityScreenOutImage = new Image();
      qualityScreenOutImage.src = `${publicAssetsPrefix}/img/quality.png`;

      const feedbackImage = new Image();
      feedbackImage.src = `${publicAssetsPrefix}/img/feedback.jpg`;

      const cowImage = new Image();
      cowImage.src = `${publicAssetsPrefix}/img/cow.png`;

      const expiredImage = new Image();
      expiredImage.src = `${publicAssetsPrefix}/img/expired.png`;

      const hybridImage = new Image();
      hybridImage.src = `${publicAssetsPrefix}/img/hybrid.png`;

      const surveyAlreadyTakenImage = new Image();
      surveyAlreadyTakenImage.src = `${publicAssetsPrefix}/img/alreadytaken.png`;

      const notActive = new Image();
      notActive.src = `${publicAssetsPrefix}/img/notactive.png`;

      const historyEmptyOffers = new Image();
      historyEmptyOffers.src = `${publicAssetsPrefix}/img/no-offers.png`;

      Pollfish.helper.hasDownloadImages = true;
    },
  };
})();

export default Pollfish.ui;
